import { RefObject, useEffect } from "react";
import { gridColumnPositionsSelector, GridApi, gridVisiblePinnedColumnDefinitionsSelector } from "@mui/x-data-grid-premium";
import { isIOS } from "@/lib/utils";
import { AXIS_THRESHOLD, releaseSpeed, resolveAxis, snapTarget, TouchAxis, TrailPoint } from "../touchAxis";

const SNAP_MS = 320;
const TRAIL_POINTS = 6;
/** Touches that belong to something else: resizing a column, or a detail panel with its own scrolling. */
const NOT_OURS = ".MuiDataGrid-columnSeparator, .MuiDataGrid-detailPanel";

/**
 * Holds a swipe on an iOS grid to one direction.
 *
 * The grid scrolls both ways in one scroller. Chromium locks a touch scroll to its main direction, iOS does not,
 * so on an iPhone a thumb moving up a list slid the columns sideways as well. `data-axis-lock` turns on
 * `touch-action: pan-y` (`tableStyleOptions`): vertical scrolling stays native, with its momentum, and iOS no
 * longer pans sideways. Sideways drags are moved from here, and stop with a column against the pinned one.
 */
export const useTouchAxisLock = (apiRef: RefObject<GridApi | null>) => {
    useEffect(() => {
        const root = apiRef.current?.rootElementRef?.current;
        if (!root || !isIOS()) return;

        root.setAttribute("data-axis-lock", "");

        let scroller: HTMLElement | null = null;
        let axis: TouchAxis | null = null;
        let startX = 0;
        let startY = 0;
        let startLeft = 0;
        let startTop = 0;
        let trail: TrailPoint[] = [];
        let frame = 0;

        const columnEdges = () => {
            const positions = gridColumnPositionsSelector(apiRef as RefObject<GridApi>);
            const pinnedLeft = gridVisiblePinnedColumnDefinitionsSelector(apiRef as RefObject<GridApi>).left.length;
            return positions.slice(pinnedLeft).map((position) => position - (positions[pinnedLeft] ?? 0));
        };

        const stopSnap = () => {
            cancelAnimationFrame(frame);
            frame = 0;
        };

        const snapTo = (target: number) => {
            if (!scroller) return;
            const element = scroller;
            const from = element.scrollLeft;
            if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
                element.scrollLeft = target;
                return;
            }
            const began = performance.now();
            const step = (now: number) => {
                const progress = Math.min(1, (now - began) / SNAP_MS);
                element.scrollLeft = from + (target - from) * (1 - Math.pow(1 - progress, 3));
                frame = progress < 1 ? requestAnimationFrame(step) : 0;
            };
            frame = requestAnimationFrame(step);
        };

        const onTouchStart = (event: TouchEvent) => {
            stopSnap();
            scroller = null;
            if (event.touches.length !== 1 || (event.target as Element).closest?.(NOT_OURS)) return;
            const candidate = root.querySelector<HTMLElement>(".MuiDataGrid-virtualScroller");
            // A locked grid hides its overflow: it does not scroll, and setting scrollLeft would still move it.
            if (!candidate || getComputedStyle(candidate).overflowX === "hidden") return;
            scroller = candidate;
            axis = null;
            trail = [];
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            startLeft = candidate.scrollLeft;
            startTop = candidate.scrollTop;
        };

        const onTouchMove = (event: TouchEvent) => {
            if (!scroller) return;
            if (event.touches.length !== 1) {
                scroller = null;
                return;
            }
            const { clientX, clientY } = event.touches[0];
            const dx = clientX - startX;
            // iOS has already started scrolling the list: that decides it, whatever the finger's slant.
            if (!axis) axis = scroller.scrollTop !== startTop ? "y" : resolveAxis(dx, clientY - startY);
            if (axis !== "x") return;
            if (event.cancelable) event.preventDefault();
            // Taking up the threshold, or the columns jump by it as the drag begins.
            scroller.scrollLeft = startLeft - (dx - Math.sign(dx) * AXIS_THRESHOLD);
            trail = [...trail.slice(1 - TRAIL_POINTS), [event.timeStamp, clientX]];
        };

        const onTouchEnd = (event: TouchEvent) => {
            if (!scroller || axis !== "x") return;
            // The drag ended on a row or a link: it was not a tap on it.
            if (event.cancelable) event.preventDefault();
            snapTo(snapTarget(columnEdges(), scroller.scrollLeft, releaseSpeed(trail, event.timeStamp), scroller.scrollWidth - scroller.clientWidth));
            axis = null;
        };

        root.addEventListener("touchstart", onTouchStart, { passive: true });
        root.addEventListener("touchmove", onTouchMove, { passive: false });
        root.addEventListener("touchend", onTouchEnd, { passive: false });
        root.addEventListener("touchcancel", onTouchEnd, { passive: false });

        return () => {
            stopSnap();
            root.removeAttribute("data-axis-lock");
            root.removeEventListener("touchstart", onTouchStart);
            root.removeEventListener("touchmove", onTouchMove);
            root.removeEventListener("touchend", onTouchEnd);
            root.removeEventListener("touchcancel", onTouchEnd);
        };
    }, [apiRef]);
};
